// @file Shared logic for uploading files (from drag-and-drop or clipboard paste) to the
// whiteboard canvas as attachment shapes.
import { ContentPickerSource } from '@@/bits/content_picker'
import { calculateUploadedFilePositionsAndDimensions, MAXIMUM_NUMBER_OF_FILES } from '@@/bits/whiteboard_upload'
import { useWhiteboardStore } from '@@/pinia/whiteboard'
import { useWhiteboardPermissionsStore } from '@@/pinia/whiteboard_permissions'
import { useWhiteboardToolsStore } from '@@/pinia/whiteboard_tools'
import { useWhiteboardUploadStore } from '@@/pinia/whiteboard_upload'
import { useWhiteboardCanvasSize } from '@@/vuecomposables/useWhiteboardCanvasSize'
import type { TLPageId } from 'tldraw'

type FileUploadBlocker = 'no-write-permission' | 'upload-tool-disabled'

function useWhiteboardFileUpload(): {
  canIUploadFiles: () => boolean
  getFileUploadBlocker: () => FileUploadBlocker | null
  uploadFilesAt: (files: File[], point: { x: number; y: number }) => Promise<void>
} {
  const whiteboardStore = useWhiteboardStore()
  const whiteboardPermissionsStore = useWhiteboardPermissionsStore()
  const whiteboardToolsStore = useWhiteboardToolsStore()
  const whiteboardUploadStore = useWhiteboardUploadStore()
  const { canvasWidth, canvasHeight } = useWhiteboardCanvasSize()

  function getFileUploadBlocker(): FileUploadBlocker | null {
    if (!whiteboardPermissionsStore.canIWriteFrame(whiteboardStore.currentFrameId as TLPageId)) {
      return 'no-write-permission'
    }
    if (!whiteboardToolsStore.isToolEnabled(ContentPickerSource.FileBrowser)) {
      return 'upload-tool-disabled'
    }
    return null
  }

  function canIUploadFiles(): boolean {
    return getFileUploadBlocker() == null
  }

  async function uploadFilesAt(files: File[], point: { x: number; y: number }): Promise<void> {
    if (files.length === 0) return
    if (!canIUploadFiles()) return

    const fileInfos = await calculateUploadedFilePositionsAndDimensions({
      files: files.length > MAXIMUM_NUMBER_OF_FILES ? files.slice(0, MAXIMUM_NUMBER_OF_FILES) : files,
      dropPosition: { x: point.x, y: point.y },
      canvasWidth: canvasWidth.value,
      canvasHeight: canvasHeight.value,
    })
    fileInfos.forEach((info) => {
      const { file, width, height, aspectRatio, x, y } = info
      whiteboardUploadStore.startNewFileUpload(file, {
        contentSource: ContentPickerSource.FileBrowser,
        x,
        y,
        width,
        height,
        aspectRatio,
      })
    })
  }

  return { canIUploadFiles, getFileUploadBlocker, uploadFilesAt }
}

export { useWhiteboardFileUpload }
